import { useAppForm } from '@/components/form';
import { faCircleNotch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@e2n/ui';
import { changePasswort } from '@/services';

export function PasswordReset() {
  const { t } = useTranslation();
  return (
    <div>
      <div className="grid md:grid-cols-6 grid-cols-1 gap-6 py-12">
        <div className="flex flex-col gap-2 md:col-span-2 col-span-full">
          <div className="flex gap-2">
            <h2 className="text-lg font-semibold leading-7 text-neutrals-900">
              {t('pages.profile.sections.changePassword.heading')}
            </h2>
          </div>
          <p className="text-sm leading-6 text-neutrals-900">
            <p>
              {t('pages.profile.sections.changePassword.description.general')}
            </p>
            <ul className="ml-5 list-disc">
              <li>
                {t('pages.profile.sections.changePassword.description.1')}
              </li>
              <li>
                {t('pages.profile.sections.changePassword.description.2')}
              </li>
              <li>
                {t('pages.profile.sections.changePassword.description.3')}
              </li>
            </ul>
          </p>
        </div>
        <div className="md:col-start-3 col-span-full 2xl:col-span-2 xl:col-span-3 content-center ml-8">
          <ChangePassword />
        </div>
      </div>
    </div>
  );
}

// eslint-disable-next-line max-lines-per-function
function ChangePassword() {
  const { t } = useTranslation();
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const schema = z
    .object({
      oldPassword: z.string().min(1, {
        message: t('pages.profile.sections.changePassword.old.invalid'),
      }),
      newPassword: z
        .string({
          message: t('pages.profile.sections.changePassword.new.empty'),
        })
        .min(12, {
          message: t('pages.profile.sections.changePassword.new.unsecure'),
        }),
      newPasswordRepeat: z.string().min(1, {
        message: t('pages.profile.sections.changePassword.repeat.empty'),
      }),
    })
    .superRefine((val, ctx) => {
      if (val.newPasswordRepeat.length < 1) {
        return;
      }
      if (val.newPassword !== val.newPasswordRepeat) {
        ctx.addIssue({
          code: 'custom',
          message: t('pages.profile.sections.changePassword.repeat.invalid'),
          path: ['newPasswordRepeat'],
        });
      }
    });

  const form = useAppForm({
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      newPasswordRepeat: '',
    } satisfies z.infer<typeof schema> as z.infer<typeof schema>,
    validators: {
      onSubmit: schema,
    },
    onSubmit: async ({ value, formApi }) => {
      try {
        const returnData = await changePasswort(
          value.oldPassword,
          value.newPassword,
        );
        if (returnData.status === 'error') {
          if (returnData.type === 'NotAuthorized') {
            formApi.setFieldMeta('oldPassword', (prev) => ({
              ...prev,
              errorMap: {
                ...prev.errorMap,
                onSubmit: {
                  message: t(
                    'pages.profile.sections.changePassword.old.invalid',
                  ),
                },
              },
            }));
            return;
          }
          setError(true);
          return;
        }
        setSuccess(true);
        setError(false);
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      } catch {
        setError(true);
      }
    },
  });

  return (
    <div className="flex w-full gap-2">
      <form
        className="flex flex-col gap-4 w-full"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}>
        <form.AppField name="oldPassword">
          {(field) => (
            <field.TextInput
              type="password"
              label={t('pages.profile.sections.changePassword.old.label')}
            />
          )}
        </form.AppField>
        <form.AppField name="newPassword">
          {(field) => (
            <field.TextInput
              type="password"
              label={t('pages.profile.sections.changePassword.new.label')}
            />
          )}
        </form.AppField>
        <form.AppField name="newPasswordRepeat">
          {(field) => (
            <field.TextInput
              type="password"
              label={t('pages.profile.sections.changePassword.repeat.label')}
            />
          )}
        </form.AppField>

        <form.Subscribe selector={(state) => [state.isSubmitting]}>
          {([isSubmitting]) => (
            <div className="flex flex-col gap-2 items-start">
              <Button
                className={`w-fit flex gap-2 transition-all duration-300 ${
                  success ? 'bg-primary-green-600' : ''
                }`}
                type="submit"
                disabled={isSubmitting}>
                {isSubmitting && (
                  <FontAwesomeIcon
                    className="animate-spin"
                    icon={faCircleNotch}
                  />
                )}
                {success === false && (
                  <span>
                    {t('pages.profile.sections.changePassword.confirm')}
                  </span>
                )}
                {success && (
                  <span>
                    {t('pages.profile.sections.changePassword.success')}
                  </span>
                )}
              </Button>
              {error && (
                <span className="text-sm text-primary-red-400 max-w-[50%]">
                  {t('pages.profile.sections.changePassword.error')}
                </span>
              )}
            </div>
          )}
        </form.Subscribe>
      </form>
    </div>
  );
}
